import React from "react";
import { useNavigate } from "react-router-dom";
import { XIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

interface RegistrationSuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RegistrationSuccessDialog: React.FC<
  RegistrationSuccessDialogProps
> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { setJustRegistered } = useAuth();

  const handleGoToDashboard = () => {
    setJustRegistered(false); // Clear the flag
    onClose();
    navigate("/dashboard");
  };

  const handleClose = () => {
    setJustRegistered(false); // Clear the flag
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-3xl p-8 max-w-md w-full relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180.69deg, #D7F5E2 0.59%, #D1F7FF 24.19%, #EFF3FB 64.64%)",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XIcon className="w-6 h-6" />
        </button>

        {/* Confetti decorations */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Left side confetti */}
          <div className="absolute top-8 left-8 w-4 h-2 bg-red-300 rounded-full transform rotate-45"></div>
          <div className="absolute top-12 left-12 w-3 h-1 bg-yellow-300 rounded-full transform -rotate-12"></div>
          <div className="absolute top-20 left-6 w-2 h-4 bg-pink-300 rounded-full transform rotate-75"></div>
          <div className="absolute top-24 left-16 w-3 h-2 bg-orange-300 rounded-full transform -rotate-30"></div>
          <div className="absolute top-32 left-10 w-2 h-3 bg-purple-300 rounded-full transform rotate-60"></div>

          {/* Right side confetti */}
          <div className="absolute top-8 right-8 w-4 h-2 bg-red-300 rounded-full transform -rotate-45"></div>
          <div className="absolute top-12 right-12 w-3 h-1 bg-yellow-300 rounded-full transform rotate-12"></div>
          <div className="absolute top-20 right-6 w-2 h-4 bg-pink-300 rounded-full transform -rotate-75"></div>
          <div className="absolute top-24 right-16 w-3 h-2 bg-orange-300 rounded-full transform rotate-30"></div>
          <div className="absolute top-32 right-10 w-2 h-3 bg-purple-300 rounded-full transform -rotate-60"></div>

          {/* Additional scattered confetti */}
          <div className="absolute top-16 left-1/4 w-2 h-2 bg-blue-300 rounded-full transform rotate-45"></div>
          <div className="absolute top-28 right-1/4 w-3 h-1 bg-green-300 rounded-full transform -rotate-30"></div>
          <div className="absolute top-36 left-1/3 w-1 h-3 bg-indigo-300 rounded-full transform rotate-90"></div>
          <div className="absolute top-40 right-1/3 w-2 h-2 bg-teal-300 rounded-full transform -rotate-45"></div>
        </div>

        {/* Success icon */}
        <div className="flex justify-center mb-6 relative z-10">
          <div className="w-24 h-24 flex items-center justify-center">
            <img
              src="/checklist.png"
              alt="Registration Success Checklist"
              className="w-20 h-20 object-contain"
            />
          </div>
        </div>

        {/* Content */}
        <div className="text-center relative z-10">
          <h2 className="text-2xl font-bold text-[#077741] mb-4">
            Pendaftaran Berhasil
          </h2>
          <p className="text-[#373e46] mb-8 leading-relaxed">
            Anda berhasil melakukan pendaftaran di akun adapteach, mari explore
            fitur adapteach!
          </p>

          {/* Dashboard button */}
          <Button
            onClick={handleGoToDashboard}
            className="w-full h-12 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg"
          >
            Ke Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};
